import type { MapTool, MapClickEvent } from "../../../application/map/tools/types";
import type { ToolDeps } from "./types";
import { movePointUseCase, renamePointUseCase } from "../../../application/map/useCases/modifyPointUseCase";
import type { SamplePointCollection } from "../../../domain/sample/types";

/** Find the sample_id of the point closest to [lng, lat], within a tolerance. */
function findNearestSample(fc: SamplePointCollection, lng: number, lat: number, toleranceDeg = 0.001): string | null {
	let best: string | null = null;
	let bestDist = toleranceDeg * toleranceDeg;
	for (const f of fc.features) {
		if (f.geometry.type !== "Point") continue;
		const [px, py] = f.geometry.coordinates;
		const d = (px - lng) ** 2 + (py - lat) ** 2;
		if (d < bestDist) {
			bestDist = d;
			best = f.properties.sample_id;
		}
	}
	return best;
}

export function makePointModifyTool(deps: ToolDeps): MapTool {
	let jobId: string | null = null;
	let selectedSampleId: string | null = null;

	function isPoint(sampleId: string): boolean {
		const fc = jobId ? deps.getPointsFC(jobId) : null;
		return !!fc?.features.some((f) => f.properties.sample_id === sampleId);
	}

	return {
		id: "point-modify",
		label: "~Pt",
		cursor: "pointer",
		onActivate() {
			jobId = deps.getSelectedJobId();
			selectedSampleId = null;
			if (!jobId) {
				deps.showError("No job selected");
				deps.deactivateTool();
				return;
			}

			const pointsFC = deps.getPointsFC(jobId);
			if (!pointsFC?.features?.length) {
				deps.showError("No points to modify");
				deps.deactivateTool();
				return;
			}
		},
		onFeatureClick(sampleId: string, e: MapClickEvent) {
			if (!jobId) return;

			// Non-point feature (e.g. boundary polygon) — treat as map click (move)
			if (!isPoint(sampleId)) {
				this.onMapClick!(e);
				return;
			}

			selectedSampleId = sampleId;
			deps.toolOverlay.clearOverlay();
			const pointsFC = deps.getPointsFC(jobId)!;
			const feature = pointsFC.features.find((f) => f.properties.sample_id === sampleId);
			if (feature) deps.toolOverlay.addMarker(feature.geometry.coordinates);
		},
		onMapClick(e: MapClickEvent) {
			if (!jobId || !selectedSampleId) return;
			const currentJobId = jobId;

			const pointsFC = deps.getPointsFC(currentJobId);
			if (!pointsFC) return;

			const newCoords = [e.lngLat.lng, e.lngLat.lat];

			const result = movePointUseCase(pointsFC, selectedSampleId, newCoords, (col) =>
				deps.applyPoints(currentJobId, col),
			);

			if (!result.ok) {
				deps.showError(result.error);
				return;
			}

			deps.undoStack.push(result.data.command);
			deps.toolOverlay.clearOverlay();
			selectedSampleId = null;
		},
		onMapDblClick(e: MapClickEvent): boolean {
			if (!jobId) return false;
			const currentJobId = jobId;

			const pointsFC = deps.getPointsFC(currentJobId);
			if (!pointsFC) return false;

			const sampleId = findNearestSample(pointsFC, e.lngLat.lng, e.lngLat.lat);
			if (!sampleId) return false;

			const newId = window.prompt("Rename sample ID:", sampleId);
			if (!newId || newId === sampleId) return true;

			const result = renamePointUseCase(pointsFC, sampleId, newId, (col) => deps.applyPoints(currentJobId, col));

			if (!result.ok) {
				deps.showError(result.error);
			} else {
				deps.undoStack.push(result.data.command);
			}
			return true;
		},
		onDeactivate() {
			deps.toolOverlay.clearOverlay();
			selectedSampleId = null;
			jobId = null;
		},
	};
}
